<template>
  <div id="fiscal-job-list">

    <!-- Sans correspondance GRTVA -> taux, aucun bon ne peut sortir. Annoncé en
         tête et en danger : c'est un blocage total, pas un réglage optionnel. -->
    <vs-alert
      v-if="vatGroups === 0"
      color="danger"
      icon-pack="feather"
      icon="icon-alert-triangle"
      class="mb-base"
    >
      <span class="font-semibold">VAT groups are not declared.</span>
      The register takes a <b>group number (1–5)</b>, not a percentage, and the rate behind each
      group is programmed inside the device — it can only be read from a printed <b>X report</b>.
      Until the mapping is declared, no fiscal receipt will be issued.
      <router-link :to="{ path: '/settings/fiscal-config' }" class="underline">Declare it here</router-link>.
    </vs-alert>

    <!-- ETAT DE L'AGENT.
         C'est le mode de panne le plus probable de l'integration : l'agent
         tourne sur une machine Windows de caisse que personne ne surveille.
         La caisse et le pilote sont visibles physiquement ; l'agent ne l'est
         pas. Sans ce bloc, « file qui ne bouge pas » et « agent mort » se
         ressemblent exactement. -->
    <vs-alert
      v-if="counts.agent_blocking"
      color="danger"
      icon-pack="feather"
      icon="icon-wifi-off"
      class="mb-base"
    >
      <span class="font-semibold">The CasaMarcat agent is not responding.</span>
      {{ counts.pending }} receipt(s) are waiting and nobody is collecting them — nothing will
      print until the agent runs again.
      <span v-if="agent"> Last seen {{ agoText }} from <b>{{ agent.name }}</b>.</span>
      <span v-else> It has never reported in.</span>
      <br />
      On the driver host, run <code>node bin/casamarcat.js ping</code> to check it, then
      <code>reportx</code> to confirm the register answers.
    </vs-alert>

    <vs-alert
      v-else-if="agent && agent.status === 'error'"
      color="warning"
      icon-pack="feather"
      icon="icon-alert-triangle"
      class="mb-base"
    >
      <span class="font-semibold">The agent is running but reports a problem.</span>
      {{ agent.last_error }}
    </vs-alert>

    <div class="vx-row mb-base">
      <div class="vx-col md:w-1/4 w-full">
        <vx-card class="text-center">
          <h2 class="mb-0">{{ counts.pending }}</h2>
          <span class="text-sm">Waiting to print</span>
        </vx-card>
      </div>
      <div class="vx-col md:w-1/4 w-full">
        <vx-card class="text-center">
          <h2 class="mb-0" :class="counts.indeterminate > 0 ? 'text-danger' : ''">
            {{ counts.indeterminate }}
          </h2>
          <span class="text-sm">Needs review</span>
        </vx-card>
      </div>
      <div class="vx-col md:w-1/4 w-full">
        <vx-card class="text-center">
          <h2 class="mb-0">{{ counts.failed }}</h2>
          <span class="text-sm">Failed</span>
        </vx-card>
      </div>
      <div class="vx-col md:w-1/4 w-full">
        <vx-card class="text-center">
          <h2 class="mb-0">{{ counts.printed_today }}</h2>
          <span class="text-sm">Printed today</span>
        </vx-card>
      </div>
    </div>

    <!-- L'état « indéterminé » n'est pas un échec de plus : le bon a PEUT-ÊTRE
         imprimé. On l'explique ici parce que c'est là que quelqu'un décide, et
         qu'un rejeu à tort encaisse deux fois le client. -->
    <vs-alert v-if="counts.indeterminate > 0" color="warning" icon-pack="feather" icon="icon-help-circle" class="mb-base">
      <span class="font-semibold">{{ counts.indeterminate }} receipt(s) need a human decision.</span>
      The driver returned <code>BONOK=-1</code> or no answer at all, which means the receipt
      <b>may have printed</b>. Check the register's electronic journal (or the next Z report),
      then tell the ERP what you found. Do not simply retry: on a card payment that would charge
      the customer twice.
    </vs-alert>

    <vx-card title="Fiscal receipts" class="mb-base">
      <div class="flex flex-wrap items-center mb-4">
        <vs-select v-model="statusFilter" class="mr-4" style="min-width:220px" label="Status">
          <vs-select-item value="" text="All" />
          <vs-select-item value="queued,leased,writing" text="In progress" />
          <vs-select-item value="unknown" text="Needs review" />
          <vs-select-item value="printed,resolved_printed" text="Printed" />
          <vs-select-item value="failed,rejected,resolved_not_printed" text="Not printed" />
        </vs-select>
        <vs-button color="primary" type="border" icon-pack="feather" icon="icon-refresh-cw" @click="load">
          Refresh
        </vs-button>
        <span v-if="loading" class="ml-4 text-sm text-grey">Loading…</span>
      </div>

      <vs-table :data="jobs" max-items="25" pagination search noDataText="No fiscal receipt yet">
        <template slot="thead">
          <vs-th>Job</vs-th>
          <vs-th>Invoice</vs-th>
          <vs-th>Client</vs-th>
          <vs-th>Amount</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Receipt n°</vs-th>
          <vs-th>Detail</vs-th>
          <vs-th>Action</vs-th>
        </template>

        <template slot-scope="{ data }">
          <vs-tr v-for="(tr, i) in data" :key="i" :data="tr">
            <vs-td :data="tr.id">#{{ tr.id }}</vs-td>

            <vs-td :data="tr.invoice_no">
              <router-link v-if="tr.idoperation" :to="{ path: '/invoice/' + tr.idoperation }" class="underline">
                {{ tr.invoice_no || tr.idoperation }}
              </router-link>
              <!-- Une commande de service n'a pas de facture, et ne doit pas
                   ressembler a un bon dont l'ERP aurait perdu la reference. -->
              <vs-chip v-else-if="tr.kind === 'service'" color="primary">
                {{ tr.service_command || 'service' }}
              </vs-chip>
              <span v-else>—</span>
            </vs-td>

            <vs-td :data="tr.company_name">{{ clientName(tr) }}</vs-td>

            <vs-td :data="tr.expected_ron">
              <span v-if="tr.kind === 'service'" class="text-grey">no amount</span>
              <span v-else>{{ tr.expected_ron.toFixed(2) }} RON</span>
            </vs-td>

            <vs-td :data="tr.status">
              <vs-chip :color="statusColor(tr.status)">{{ statusLabel(tr.status) }}</vs-chip>
            </vs-td>

            <vs-td :data="tr.receipt_no">{{ tr.receipt_no || '—' }}</vs-td>

            <vs-td :data="tr.err_info">
              <span class="text-sm">{{ detail(tr) }}</span>
            </vs-td>

            <vs-td>
              <!-- Seul un job indéterminé s'arbitre. Un job abouti ou
                   franchement échoué n'a rien à arbitrer, et le proposer
                   inviterait à « corriger » un bon déjà imprimé. -->
              <vs-button
                v-if="tr.can_resolve"
                size="small"
                color="warning"
                @click="openResolve(tr)"
              >Review</vs-button>
              <span v-else class="text-sm text-grey">—</span>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>

    <!-- Arbitrage : une seule question, factuelle. -->
    <vs-popup title="Did this receipt print?" :active.sync="resolveOpen">
      <div v-if="resolving">
        <p class="mb-4">
          Job <b>#{{ resolving.id }}</b> — invoice <b>{{ resolving.invoice_no || resolving.idoperation }}</b>,
          <b>{{ resolving.expected_ron.toFixed(2) }} RON</b>.
        </p>

        <vs-alert color="warning" class="mb-4">
          Check the register's <b>electronic journal</b>, or the next <b>Z report</b>. The answer
          must come from the device, not from a guess: saying “not printed” unblocks re-issuing
          this invoice, and if it had in fact printed the customer would be charged twice.
        </vs-alert>

        <label class="vs-input--label block">Commands sent to the register</label>
        <pre class="p-3 text-xs bg-grey-light rounded overflow-x-auto mb-4">{{ resolving.command_text }}</pre>

        <vs-input
          v-model="resolveReceiptNo"
          label="Receipt number (NRBON), if visible in the journal"
          class="w-full mb-4"
          placeholder="optional"
        />
        <vs-textarea v-model="resolveNote" label="Note" rows="2" class="w-full mb-4" />

        <div class="flex flex-wrap">
          <vs-button color="success" class="mr-3 mb-2" :disabled="busy" @click="submitResolve('printed')">
            It printed
          </vs-button>
          <vs-button color="danger" type="border" class="mr-3 mb-2" :disabled="busy" @click="submitResolve('not_printed')">
            Nothing printed — allow re-issue
          </vs-button>
          <vs-button color="dark" type="flat" class="mb-2" :disabled="busy" @click="resolveOpen = false">
            Cancel
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <vx-card title="CasaMarcat agent" class="mb-base">
      <fiscal-agent-status :agent="agent" :pending="counts.pending" :register="counts.register" class="mb-4" />

      <div v-if="agent">
        <div class="vx-row">
          <div class="vx-col md:w-1/2 w-full">
            <p><b>Host</b> — {{ agent.name }}</p>
            <p><b>Last seen</b> — {{ agoText }}</p>
            <p><b>Version</b> — {{ agent.version || '—' }}</p>
            <p><b>Last command</b> — {{ agent.last_command || '—' }}</p>
          </div>
          <div class="vx-col md:w-1/2 w-full">
            <!-- Les dossiers que l'agent surveille REELLEMENT. Les voir ici
                 evite le diagnostic le plus penible : un agent bien vivant qui
                 ecrit dans un dossier que le pilote ne regarde pas. -->
            <p><b>Writes commands to</b><br /><code>{{ agent.bonuri || '—' }}</code></p>
            <p class="mt-2"><b>Reads answers from</b><br /><code>{{ agent.raspuns || '—' }}</code></p>
          </div>
        </div>
        <vs-alert v-if="agent.last_error" color="warning" class="mt-4">{{ agent.last_error }}</vs-alert>
      </div>

      <div v-else>
        <p class="mb-2">
          The agent has never reported in. It runs on the computer connected to the register
          (<code>192.168.1.249</code>) and is the only thing that can write to the driver's
          <code>\Bonuri</code> folder — a browser cannot, and the server has no route to that network.
        </p>
        <p class="text-sm text-grey">
          Install it from <code>XYLO-X/CasaMarcat</code>, then run
          <code>node bin/casamarcat.js ping</code>. It will appear here.
        </p>
      </div>
    </vx-card>

  </div>
</template>

<script>
import axios from '@/axios.js'
import FiscalAgentStatus from '@/components/FiscalAgentStatus.vue'

// Libellés et couleurs au même endroit : un statut affiché différemment d'un
// écran à l'autre rend la file incompréhensible.
const STATUS = {
  queued:               { label: 'Queued',            color: 'primary' },
  leased:               { label: 'Taken by agent',    color: 'primary' },
  writing:              { label: 'Printing',          color: 'warning' },
  printed:              { label: 'Printed',           color: 'success' },
  resolved_printed:     { label: 'Printed (reviewed)', color: 'success' },
  failed:               { label: 'Failed',            color: 'danger' },
  rejected:             { label: 'Rejected',          color: 'danger' },
  // Jamais en rouge « échec » : l'utilisateur rejouerait. L'orange dit
  // « il faut regarder », ce qui est exactement l'action attendue.
  unknown:              { label: 'Needs review',      color: 'warning' },
  resolved_not_printed: { label: 'Not printed',       color: 'dark' },
}

export default {
  components: { FiscalAgentStatus },
  data() {
    return {
      jobs: [],
      counts: { pending: 0, indeterminate: 0, failed: 0, printed_today: 0 },
      vatGroups: 0,
      agent: null,
      statusFilter: '',
      loading: false,
      busy: false,
      resolveOpen: false,
      resolving: null,
      resolveReceiptNo: '',
      resolveNote: '',
    }
  },
  watch: {
    statusFilter() { this.load() },
  },
  created() {
    this.load()
  },
  computed: {
    /** « il y a 2 min » plutôt qu'un timestamp : c'est la fraîcheur qui compte. */
    agoText() {
      if (!this.agent) return 'never'
      const s = Number(this.agent.seconds_ago)
      if (!Number.isFinite(s)) return 'unknown'
      if (s < 60) return `${s} s ago`
      if (s < 3600) return `${Math.round(s / 60)} min ago`
      if (s < 86400) return `${Math.round(s / 3600)} h ago`
      return `${Math.round(s / 86400)} day(s) ago`
    },
  },
  methods: {
    clientName(tr) {
      if (tr.company_name) return tr.company_name
      const n = [tr.firstname, tr.lastname].filter(Boolean).join(' ')
      return n || '—'
    },

    statusLabel(s) { return (STATUS[s] && STATUS[s].label) || s },
    statusColor(s) { return (STATUS[s] && STATUS[s].color) || 'primary' },

    detail(tr) {
      if (tr.err_info) return tr.err_info
      if (tr.err_code) return `Error ${tr.err_code}`
      if (tr.status === 'unknown') return 'No usable answer from the driver'
      if (tr.resolution_note) return tr.resolution_note
      return '—'
    },

    load() {
      this.loading = true
      const q = this.statusFilter ? `?status=${encodeURIComponent(this.statusFilter)}` : ''
      Promise.all([
        axios.get(`/fiscal/jobs${q}`),
        axios.get('/fiscal/counts'),
      ])
        .then(([jobsRes, countsRes]) => {
          this.jobs = jobsRes.data || []
          this.counts = countsRes.data || this.counts
          this.vatGroups = countsRes.data ? countsRes.data.vat_groups_declared : 0
          this.agent = (countsRes.data && countsRes.data.agent) || null
        })
        .catch((err) => {
          this.$vs.notify({
            color: 'danger',
            title: 'Could not load fiscal receipts',
            text: (err.response && err.response.data && err.response.data.error) || err.message,
          })
        })
        .finally(() => { this.loading = false })
    },

    openResolve(tr) {
      this.resolving = tr
      this.resolveReceiptNo = tr.receipt_no || ''
      this.resolveNote = ''
      this.resolveOpen = true
    },

    submitResolve(outcome) {
      this.busy = true
      axios.post(`/fiscal/job/${this.resolving.id}/resolve`, {
        outcome,
        receipt_no: this.resolveReceiptNo || undefined,
        note: this.resolveNote || undefined,
      })
        .then(() => {
          this.$vs.notify({
            color: 'success',
            title: 'Recorded',
            text: outcome === 'printed'
              ? 'Marked as printed.'
              : 'Marked as not printed — this invoice can be issued again.',
          })
          this.resolveOpen = false
          this.load()
        })
        .catch((err) => {
          this.$vs.notify({
            color: 'danger',
            title: 'Could not record the decision',
            text: (err.response && err.response.data && err.response.data.error) || err.message,
          })
        })
        .finally(() => { this.busy = false })
    },
  },
}
</script>
